import { onCLS, onINP, onLCP } from 'web-vitals';

/*
 * Web vitals beacon (perf facts). On a sample of the pages a signed-in architect loads with a FULL navigation,
 * measure LCP, INP and CLS with Google's `web-vitals` and post them, in ONE beacon, to Perf::WebVitalsController,
 * which records each as a Sentry Application Metric per route. The nightly perf facts read those metrics.
 *
 * Opt-in by the server: nothing happens without `<meta name="web-vitals" ...>`, rendered only by the
 * signed-in app layout (`application.html.erb`, helper `web_vitals_meta_tag`). The meta carries the route
 * (`controller_path#action`, never a URL), the endpoint and the sample rate; this file builds none of them.
 *
 * One page, one measure: the values belong to the page of the full navigation. INP and CLS would otherwise keep
 * accumulating over the Turbo visits that follow and be filed under that first route, so the beacon leaves at
 * the first of: the page hidden (tab switch, close), or the first Turbo visit; then measuring stops. LCP, INP and CLS
 * use `reportAllChanges` so their current value is known when a visit starts. Pages reached by a Turbo visit are
 * not measured (that would need the Soft Navigations API, Chromium-only so far).
 *
 * ONE beacon per page, not one per vital: each beacon crosses the whole ApplicationController stack.
 * `sendBeacon`: the request survives the tab closing. The CSRF token travels in the form body
 * (`authenticity_token`), read when sending.
 */
export function installWebVitalsBeacon({
  doc = document,
  win = window,
  nav = navigator,
  random = Math.random,
  vitals = { onCLS, onINP, onLCP },
} = {}) {
  const meta = doc.querySelector('meta[name="web-vitals"]');
  if (!meta || typeof nav.sendBeacon !== 'function') return false;

  const rate = Number(meta.dataset.sampleRate);
  if (!(rate > 0) || random() >= rate) return false;

  const route = meta.content;
  const url = meta.dataset.url;
  const measured = new Map();
  let done = false;

  const collect = (metric) => {
    if (!done) measured.set(metric.name, metric);
  };
  const flush = () => {
    if (done || measured.size === 0) return;
    done = true;
    const body = new FormData();
    body.append('route', route);
    for (const metric of measured.values()) {
      body.append(`vitals[${metric.name}][value]`, String(metric.value));
      body.append(`vitals[${metric.name}][rating]`, metric.rating);
    }
    const token = doc.querySelector('meta[name="csrf-token"]')?.content;
    if (token) body.append('authenticity_token', token);
    nav.sendBeacon(url, body);
  };

  // reportAllChanges on LCP too: web-vitals only FINALIZES it after the first click, key or hide, inside an idle
  // callback (lib/whenIdleOrHidden.js), so a click on a link sent the beacon on `turbo:before-visit` before LCP
  // arrived (staging 18/10: 2 LCP for 10 CLS). Every candidate is collected as it appears; the last one leaves.
  vitals.onLCP(collect, { reportAllChanges: true });
  vitals.onINP(collect, { reportAllChanges: true });
  vitals.onCLS(collect, { reportAllChanges: true });
  // Registered AFTER web-vitals' own listeners, which report CLS and INP on the same `visibilitychange`.
  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'hidden') flush();
  });
  win.addEventListener('pagehide', flush);
  doc.addEventListener('turbo:before-visit', flush);
  return true;
}
